<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	title: $.text('Title'),
	variant: $.select('Archive Type', [
		{ label: 'Interview', value: 'interview' },
		{ label: 'Op-ed', value: 'op-ed' },
		{ label: 'Blog', value: 'blog' },
		{ label: 'Israel-Hamas War', value: 'israel-hamas-war' },
	]),
})
export const BlockCurrentArchive = defineBlock({
	thumbnail: '/block-thumbs/BlockCurrentArchive.svg',
	category: 'Listings',
	order: 116,
	name: 'BlockCurrentArchive',
	label: 'Current Archive',
	icon: 'view_list',
	schema,
	defaults: { title: 'Interview', variant: 'interview' },
	dataSource: { collection: 'blogs', store: 'the_torah', label: 'Current Articles' },
})
</script>
<script setup lang="ts">
import { computed } from 'vue'

import { Skeleton } from '@bagelink/vue'
import BlockArticleList from '@/components/blocks/BlockArticleList.vue'
import { ensureAuthors, resolveAuthorForBlock, useCollection } from '@/composables/useDatastore'

type ArchiveVariant = 'interview' | 'op-ed' | 'blog' | 'israel-hamas-war'

const TYPE_IDS: Record<ArchiveVariant, string> = {
	interview: '5cee70f48e610adba3c75e7e',
	'op-ed': '5cee70f48e610a3d07c75e7d',
	blog: '5cee70f48e610a27a1c75ecf',
	'israel-hamas-war': '65356f6ebab6f55792f66065',
}



const props = withDefaults(defineProps<{ title?: string; variant?: ArchiveVariant }>(), {
	title: 'Interview',
	variant: 'interview',
})

ensureAuthors()
const { items, loading } = useCollection<Record<string, any>>('blogs', {
	limit: 200,
	pick: [
		'id',
		'_id',
		'slug',
		'title',
		'teaser',
		'metaDescription',
		'image',
		'publishDate',
		'blogTypeId',
		'authorIds',
		'authorId',
		'secondAuthorId',
	],
})

function plainText(value = '') {
	return value
		.replace(/<[^>]*>/g, ' ')
		.replace(/&mdash;/g, '—')
		.replace(/&ndash;/g, '–')
		.replace(/&rsquo;/g, '’')
		.replace(/&ldquo;|&rdquo;/g, '"')
		.replace(/&amp;/g, '&')
		.replace(/&nbsp;/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
}

function resolveArticleAuthors(article: Record<string, any>) {
	if (Array.isArray(article.authors) && article.authors.length) return article.authors
	const authorIds =
		Array.isArray(article.authorIds) && article.authorIds.length
			? article.authorIds
			: [article.authorId, article.secondAuthorId, article.thirdAuthorId].filter(Boolean)
	return authorIds
		.map((id: string) => resolveAuthorForBlock(id))
		.filter(Boolean)
		.map((author: any) => ({
			name: author.name,
			title: author.title,
			image: author.image,
			link: author.slug ? `/author/${author.slug}` : '/authors',
		}))
}

const archiveArticles = computed(() =>
	items.value
		.filter(article => article.blogTypeId === TYPE_IDS[props.variant])
		.sort((a, b) => new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime())
		.map(article => {
			return {
				title: article.title || '',
				subtitle: plainText(article.teaser || article.metaDescription || ''),
				image: article.image || '/og.jpg',
				link: `/blog/${article.slug}`,
				authors: resolveArticleAuthors(article),
			}
		}),
)
</script>

<template>
	<section class="pt-4 pb-3 m_pt-2 m_pb-2 m_px-1">
		<h1
			class="w1000px mx-auto mt-0 mb-0 txt40 regular line-height-11 balance pb-3 txt-center m_px-1 m_txt32 m_line-height-12 m_pb-2"
		>
			{{ props.title }}
		</h1>

		<div
			v-if="loading"
			class="w1000px flex py-2 m_pt-05"
			aria-label="Loading archive articles"
			aria-busy="true"
		>
			<div class="w720px mx-auto display-flex column gap-3 m_w-100i">
				<div
					v-for="article in 5"
					:key="article"
					class="display-flex gap-1 align-items-start m_block"
				>
					<Skeleton class="aspect-ratio-4-3 h-110px flex-shrink-0 mb-0 m_h-200px m_w-100p m_mb-1" />
					<div class="flex-grow min-w-0">
						<Skeleton width="88%" height="24px" class="mb-05" />
						<Skeleton width="55%" height="24px" class="mb-1" />
						<Skeleton width="100%" height="15px" class="mb-025" />
						<Skeleton width="82%" height="15px" class="mb-075" />
						<Skeleton width="38%" height="14px" class="mb-0" />
					</div>
				</div>
			</div>
		</div>

		<BlockArticleList
			v-else
			:articles="archiveArticles"
			centered="false"
			show-topics="false"
			show-authors="true"
		/>
	</section>
</template>
